import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import { useCourses } from '../../hooks/useCourseData';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Image from '../../components/AppImage';

const CoursesCatalog = () => {
  const navigate = useNavigate();
  const { courses, loading, error } = useCourses();

  const handleCourseClick = (course) => {
    navigate(`/course/${course.slug}`);
  };

  const handleGoToModules = (course) => {
    navigate(`/course/${course.slug}/modules`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Catálogo de Cursos - Academy Platform</title>
        </Helmet>
        <Header />
        <main className="pt-16">
          <div className="container mx-auto px-6 py-8">
            <div className="h-8 bg-muted rounded w-64 mb-6 animate-pulse"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 bg-muted rounded-lg animate-pulse"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Erro - Academy Platform</title>
        </Helmet>
        <Header />
        <main className="pt-16">
          <div className="container mx-auto px-6 py-8">
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" size={32} className="text-destructive" />
              </div>
              <h2 className="text-2xl font-semibold mb-2">Erro ao carregar cursos</h2>
              <p className="text-muted-foreground mb-4">{error.message}</p>
              <Button onClick={() => window.location.reload()}>
                Tentar Novamente
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Catálogo de Cursos - Academy Platform</title>
        <meta name="description" content="Explore todos os cursos disponíveis na plataforma." />
      </Helmet>
      
      <Header />
      
      <main className="pt-16">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Catálogo de Cursos
            </h1>
            <p className="text-muted-foreground">
              Explore todos os cursos disponíveis para seu desenvolvimento
            </p>
          </div>

          {/* Courses Grid */}
          {courses.filter(course => course.status !== 'archived').length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.filter(course => course.status !== 'archived').map((course) => {
                const isDraft = course.status === 'draft';
                return (
                <div
                  key={course.id}
                  className={`bg-card rounded-lg overflow-hidden shadow-tier-1 hover:shadow-tier-2 transition-all duration-300 ${
                    isDraft ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                  } group`}
                  onClick={() => !isDraft && handleCourseClick(course)}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video overflow-hidden">
                    <Image
                      src={course.cover || '/assets/images/no_image.png'}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                    
                    {/* Play Button / Lock Icon */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {isDraft ? (
                        <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                          <Icon name="Lock" size={20} className="text-muted-foreground" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center backdrop-blur-sm">
                          <Icon name="Play" size={20} className="text-primary ml-1" />
                        </div>
                      )}
                    </div>

                    {/* Status Badge */}
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        course.status === 'published' 
                          ? 'bg-success text-success-foreground' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {course.status === 'published' ? 'Disponível' : 'Em Breve'}
                      </span>
                    </div>
                    
                    {/* Locked Badge */}
                    {isDraft && (
                      <div className="absolute top-2 right-2">
                        <span className="bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs font-medium">
                          Bloqueado
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-card-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {course.title}
                    </h3>
                    
                    {course.summary && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2 font-caption">
                        {course.summary}
                      </p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <Button
                        variant={isDraft ? "ghost" : "default"}
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDraft) {
                            handleCourseClick(course);
                          }
                        }}
                        disabled={isDraft}
                        iconName={isDraft ? "Lock" : undefined}
                        iconPosition={isDraft ? "left" : undefined}
                        iconSize={isDraft ? 14 : undefined}
                        className="flex-1"
                      >
                        {isDraft ? "Bloqueado" : "Ver Curso"}
                      </Button>
                      <Button
                        variant={isDraft ? "ghost" : "outline"}
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDraft) {
                            handleGoToModules(course);
                          }
                        }}
                        disabled={isDraft}
                        iconName={isDraft ? "Lock" : undefined}
                        iconPosition={isDraft ? "left" : undefined}
                        iconSize={isDraft ? 14 : undefined}
                        className="flex-1"
                      >
                        {isDraft ? "Bloqueado" : "Ir para Módulos"}
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="BookOpen" size={24} className="text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Nenhum curso disponível</h3>
              <p className="text-muted-foreground mb-4">
                Não há cursos cadastrados no momento.
              </p>
              <Button onClick={() => navigate('/dashboard-home')}>
                Voltar ao Início
              </Button>
            </div>
          )}
        </div>
      </main>
      
      <FloatingAIChat />
    </div>
  );
};

export default CoursesCatalog;



































